// PointsAdminPage — super-admin only.
// Search any user by email, see their balance + history, adjust ± with reason,
// and edit the global loyalty config.
//
// Two collapsible panels:
//   1. Loyalty Config (earn rate, redeem ratio, etc.)
//   2. User Lookup → Adjust + History

import { useEffect, useState, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Navbar } from '../../components/Navbar';
import { ReviewRewardSwitches } from '../../components/ReviewRewardSwitches';
import { ReviewWindowSettings } from '../../components/reviews/ReviewWindowSettings';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import {
  ArrowLeft, Search, Sparkles, Settings, Plus, Minus, History, Save,
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/+$/, "");

const NUMERIC_FIELDS = [
  'earn_rate',
  'pts_per_dollar_redeem',
  'min_redemption_pts',
  'max_redemption_percent',
  'referral_referrer_pts',
  'referral_referee_pts',
  'staff_qr_signup_pts',
  'staff_qr_first_order_pts',
  'customer_qr_welcome_pts',
  'expiration_months',
  // Phase 5.9.7 — operator economics (all flag-gated; values applied when flag is ON)
  'earn_cap_daily',
  'min_order_for_earn',
  'dormant_nudge_after_days',
  // Reviews
  'review_launch_bonus_pts',
  'review_approval_pts',
  'review_like_pts_per_like',
  'review_like_max_eligible_per_review',
  'review_daily_limit',
  'review_photo_limit',
];

const FIELD_LABELS = {
  earn_rate: 'Earn rate (pts per $1)',
  earn_rounding: 'Earn rounding',
  pts_per_dollar_redeem: 'Redeem ratio (pts per $1)',
  min_redemption_pts: 'Minimum redemption (pts)',
  max_redemption_percent: 'Max % of subtotal redeemable',
  referral_referrer_pts: 'Referral: referrer bonus',
  referral_referee_pts: 'Referral: referee bonus',
  staff_qr_signup_pts: 'Staff QR signup bonus',
  staff_qr_first_order_pts: 'Staff QR first-order bonus',
  customer_qr_welcome_pts: 'Customer welcome bonus',
  expiration_months: 'Point expiration (months, 0=never)',
  cancel_reverses_points: 'Reverse points on cancellation',
  // Phase 5.9.7 — operator economics
  earn_cap_daily: 'Max pts a customer can earn per day (flag: loyalty.earn_cap_daily)',
  min_order_for_earn: 'Minimum order subtotal to earn (flag: loyalty.min_order_for_earn)',
  dormant_nudge_after_days: 'Days idle before "use \'em" nudge fires (flag: loyalty.dormant_nudge)',
  review_launch_bonus_pts: 'Review: launch bonus pts (when launch bonus ON)',
  review_approval_pts: 'Review: normal approval pts',
  review_like_pts_per_like: 'Review: pts per 🔥 like',
  review_like_max_eligible_per_review: 'Review: max paid likes per review',
  review_daily_limit: 'Review: max new reviews per day (0 = no limit)',
  review_photo_limit: 'Review: max photos per review (0–3, 0 = no photos)',
};

export function PointsAdminPage() {
  // Config
  const [config, setConfig] = useState(null);
  const [configDraft, setConfigDraft] = useState({});
  const [savingConfig, setSavingConfig] = useState(false);

  // User search + adjust
  // Phase 5.9.23 — was email-only LOOKUP. Now: debounced typeahead by name
  // OR email via /api/admin/users (same endpoint the User Admin page uses).
  // Operator types → dropdown → select → balance loads automatically.
  const [searchQuery, setSearchQuery] = useState('');
  const [searchedUser, setSearchedUser] = useState(null);
  const [searchedBalance, setSearchedBalance] = useState(null);
  const [typeaheadResults, setTypeaheadResults] = useState([]);
  const [typeaheadOpen, setTypeaheadOpen] = useState(false);
  const [typeaheadLoading, setTypeaheadLoading] = useState(false);
  const [typeaheadActiveIdx, setTypeaheadActiveIdx] = useState(-1);
  const typeaheadRef = useRef(null);
  const typeaheadDebounceRef = useRef(null);
  const typeaheadAbortRef = useRef(null);
  const [adjustDelta, setAdjustDelta] = useState(0);
  const [adjustReason, setAdjustReason] = useState('');
  const [adjustRegion, setAdjustRegion] = useState('dmv');
  const [adjusting, setAdjusting] = useState(false);
  const [regions, setRegions] = useState([]);

  const loadConfig = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/loyalty/config`, { withCredentials: true });
      setConfig(data);
      setConfigDraft(data);
    } catch (_) {
      toast.error('Failed to load config');
    }
  }, []);

  const loadRegions = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/regions`, { withCredentials: true });
      setRegions(data?.regions || []);
    } catch (_) {/* ignore */}
  }, []);

  useEffect(() => { loadConfig(); loadRegions(); }, [loadConfig, loadRegions]);

  const saveConfig = async () => {
    setSavingConfig(true);
    try {
      const payload = {};
      for (const k of [...NUMERIC_FIELDS, 'cancel_reverses_points', 'earn_rounding', 'earn_excluded_categories']) {
        if (configDraft[k] !== undefined && configDraft[k] !== null) {
          payload[k] = configDraft[k];
        }
      }
      const { data } = await axios.patch(
        `${API_URL}/api/loyalty/config`,
        payload,
        { withCredentials: true }
      );
      setConfig(data);
      setConfigDraft(data);
      toast.success('Loyalty config updated');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Save failed');
    } finally {
      setSavingConfig(false);
    }
  };

  // Phase 5.9.23 — debounced typeahead (mirrors UsersAdminPage pattern).
  // Fires GET /api/admin/users?q=... at 300ms after input changes, aborts
  // in-flight requests on new input, skips below 2 chars.
  useEffect(() => {
    if (typeaheadDebounceRef.current) clearTimeout(typeaheadDebounceRef.current);
    if (typeaheadAbortRef.current) typeaheadAbortRef.current.abort();
    const term = searchQuery.trim();
    typeaheadDebounceRef.current = setTimeout(async () => {
      if (term.length < 2) {
        setTypeaheadResults([]);
        setTypeaheadOpen(false);
        setTypeaheadLoading(false);
        return;
      }
      setTypeaheadLoading(true);
      const ctrl = new AbortController();
      typeaheadAbortRef.current = ctrl;
      try {
        const params = new URLSearchParams({ q: term, limit: '8' });
        const { data } = await axios.get(
          `${API_URL}/api/admin/users?${params}`,
          { withCredentials: true, signal: ctrl.signal },
        );
        setTypeaheadResults((data.users || []).slice(0, 8));
        setTypeaheadOpen(true);
        setTypeaheadActiveIdx(-1);
      } catch (e) {
        if (!axios.isCancel(e) && e.name !== 'CanceledError') {
          console.debug('points typeahead error:', e);
        }
      } finally {
        setTypeaheadLoading(false);
      }
    }, 300);
    return () => {
      if (typeaheadDebounceRef.current) clearTimeout(typeaheadDebounceRef.current);
    };
  }, [searchQuery]);

  // Close typeahead on outside click + Esc.
  useEffect(() => {
    const onClick = (e) => {
      if (typeaheadRef.current && !typeaheadRef.current.contains(e.target)) {
        setTypeaheadOpen(false);
      }
    };
    const onKey = (e) => {
      if (e.key === 'Escape') setTypeaheadOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onKey);
    };
  }, []);

  const pickTypeaheadResult = async (u) => {
    setTypeaheadOpen(false);
    setSearchQuery(u.name || u.email || '');
    setTypeaheadResults([]);
    setTypeaheadActiveIdx(-1);
    setSearchedUser(u);
    setSearchedBalance(null);
    try {
      const { data } = await axios.get(
        `${API_URL}/api/loyalty/users/${u.id}/balance`,
        { withCredentials: true }
      );
      setSearchedBalance(data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load balance');
    }
  };

  const adjust = async (sign) => {
    if (!searchedUser) return;
    const delta = Math.abs(Number(adjustDelta || 0)) * (sign === '-' ? -1 : 1);
    if (!delta) {
      toast.error('Delta must be non-zero');
      return;
    }
    if (!adjustReason.trim()) {
      toast.error('Reason is required');
      return;
    }
    setAdjusting(true);
    try {
      const { data } = await axios.post(
        `${API_URL}/api/loyalty/adjust`,
        {
          user_id: searchedUser.id,
          delta,
          reason: adjustReason.trim(),
          region: adjustRegion || null,
        },
        { withCredentials: true }
      );
      toast.success(`Applied ${delta >= 0 ? '+' : ''}${delta} pts → new balance ${data.new_balance}`);
      setAdjustDelta(0);
      setAdjustReason('');
      // Refresh balance
      const updated = await axios.get(
        `${API_URL}/api/loyalty/users/${searchedUser.id}/balance`,
        { withCredentials: true }
      );
      setSearchedBalance(updated.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Adjust failed');
    } finally {
      setAdjusting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#050003] dispatch-safe-top" data-testid="points-admin-page">
      <Navbar />
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <Link to="/dispatch" className="inline-flex items-center text-[#4B6B52] hover:text-[#39FF14] font-mono text-sm mb-2">
            <ArrowLeft className="w-4 h-4 mr-1" /> BACK TO DISPATCH
          </Link>
          <h1 className="text-[#39FF14] font-mono text-2xl font-bold neon-text">
            {'>'} POINTS_ADMIN
          </h1>
          <p className="text-[#4B6B52] font-mono text-sm mt-1">
            // SUPER-ADMIN ONLY — adjust points + edit loyalty config
          </p>
        </div>

        {/* Config panel */}
        <div className="bg-[#0C0612] border border-[#2A0F3D] p-6 mb-8">
          <p className="text-[#39FF14] font-mono text-sm font-bold uppercase mb-4 flex items-center gap-2">
            <Settings className="w-4 h-4" /> // LOYALTY CONFIG
          </p>
          {!config ? (
            <p className="text-[#4B6B52] font-mono text-sm">// loading...</p>
          ) : (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {NUMERIC_FIELDS.map((k) => (
                  <div key={k}>
                    <Label className="text-[#A3C7A8] font-mono text-xs uppercase mb-1 block">
                      {FIELD_LABELS[k]}
                    </Label>
                    <Input
                      type="number"
                      step={k === 'earn_rate' ? '0.1' : '1'}
                      value={configDraft[k] ?? ''}
                      onChange={(e) => setConfigDraft((d) => ({ ...d, [k]: Number(e.target.value) }))}
                      className="bg-[#050003] border-[#2A0F3D] text-[#39FF14] font-mono focus:border-[#39FF14] rounded-none"
                      data-testid={`config-${k}`}
                    />
                  </div>
                ))}
                <div>
                  <Label className="text-[#A3C7A8] font-mono text-xs uppercase mb-1 block">
                    {FIELD_LABELS.earn_rounding}
                  </Label>
                  <select
                    value={configDraft.earn_rounding || 'round'}
                    onChange={(e) => setConfigDraft((d) => ({ ...d, earn_rounding: e.target.value }))}
                    className="bg-[#050003] border border-[#2A0F3D] text-[#39FF14] font-mono focus:border-[#39FF14] rounded-none w-full h-10 px-3"
                    data-testid="config-earn_rounding"
                  >
                    <option value="round">round</option>
                    <option value="floor">floor</option>
                    <option value="ceil">ceil</option>
                  </select>
                </div>
                <div className="flex items-end">
                  <label className="flex items-center gap-2 text-[#A3C7A8] font-mono text-xs cursor-pointer">
                    <input
                      type="checkbox"
                      checked={!!configDraft.cancel_reverses_points}
                      onChange={(e) => setConfigDraft((d) => ({ ...d, cancel_reverses_points: e.target.checked }))}
                      className="accent-[#39FF14]"
                      data-testid="config-cancel_reverses_points"
                    />
                    {FIELD_LABELS.cancel_reverses_points}
                  </label>
                </div>
              </div>

              {/* Phase 5.9.7 — Category exclusions for earning (flag-gated by
                  loyalty.earn_excluded_categories). When the flag is ON, line
                  items in any of these categories earn 0 points. */}
              <div className="mt-4 p-3 bg-[#050003] border border-[#FF9F0A]/30">
                <Label className="text-[#FF9F0A] font-mono text-xs uppercase mb-1 block">
                  Categories excluded from earning (flag: loyalty.earn_excluded_categories)
                </Label>
                <p className="text-[#4B6B52] font-mono text-[10px] mb-2">
                  When the flag is ON, these categories earn ZERO points (protect low-margin items).
                </p>
                <div className="flex flex-wrap gap-2" data-testid="config-excluded-categories">
                  {['flower', 'edibles', 'concentrates', 'merch'].map((cat) => {
                    const active = (configDraft.earn_excluded_categories || []).includes(cat);
                    return (
                      <button
                        key={cat}
                        type="button"
                        onClick={() => {
                          setConfigDraft((d) => {
                            const cur = new Set(d.earn_excluded_categories || []);
                            if (cur.has(cat)) cur.delete(cat); else cur.add(cat);
                            return { ...d, earn_excluded_categories: Array.from(cur) };
                          });
                        }}
                        data-testid={`config-exclude-${cat}`}
                        className={`px-3 py-1.5 border font-mono text-[10px] uppercase tracking-wider transition-all ${
                          active
                            ? 'border-[#FF9F0A] text-[#FF9F0A] bg-[rgba(255,159,10,0.08)]'
                            : 'border-[#2A0F3D] text-[#4B6B52] hover:border-[#4B6B52] hover:text-[#A3C7A8]'
                        }`}
                      >
                        {active ? '✓ ' : ''}{cat.toUpperCase()}
                      </button>
                    );
                  })}
                </div>
              </div>
              <div className="mt-6"><ReviewWindowSettings /></div>
              <ReviewRewardSwitches config={config} onSaved={(data) => {
                setConfig(data);
                setConfigDraft((d) => ({ ...d, review_launch_bonus_active: data.review_launch_bonus_active, review_like_verified_only: data.review_like_verified_only }));
              }} />
              <div className="flex justify-end mt-4">
                <Button
                  onClick={saveConfig}
                  disabled={savingConfig}
                  className="bg-[#39FF14] text-[#050003] font-bold hover:bg-[#4AF626] rounded-none"
                  data-testid="save-config-btn"
                >
                  <Save className="w-4 h-4 mr-2" />
                  {savingConfig ? 'SAVING...' : 'SAVE CONFIG'}
                </Button>
              </div>
            </>
          )}
        </div>

        {/* User lookup + adjust panel */}
        <div className="bg-[#0C0612] border border-[#2A0F3D] p-6">
          <p className="text-[#39FF14] font-mono text-sm font-bold uppercase mb-4 flex items-center gap-2">
            <Sparkles className="w-4 h-4" /> // ADJUST USER POINTS
          </p>

          <div className="mb-4">
            <Label className="text-[#A3C7A8] font-mono text-xs uppercase mb-1 block">USER NAME OR EMAIL</Label>
            <div className="relative" ref={typeaheadRef}>
              <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-4 h-4 text-[#4B6B52] z-10" />
              <Input
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  // Clear selection when operator edits the query
                  if (searchedUser) { setSearchedUser(null); setSearchedBalance(null); }
                }}
                onFocus={() => { if (typeaheadResults.length > 0) setTypeaheadOpen(true); }}
                onKeyDown={(e) => {
                  if (typeaheadOpen && typeaheadResults.length > 0) {
                    if (e.key === 'ArrowDown') {
                      e.preventDefault();
                      setTypeaheadActiveIdx((i) => Math.min(i + 1, typeaheadResults.length - 1));
                      return;
                    }
                    if (e.key === 'ArrowUp') {
                      e.preventDefault();
                      setTypeaheadActiveIdx((i) => Math.max(i - 1, -1));
                      return;
                    }
                    if (e.key === 'Enter' && typeaheadActiveIdx >= 0) {
                      e.preventDefault();
                      pickTypeaheadResult(typeaheadResults[typeaheadActiveIdx]);
                      return;
                    }
                  }
                }}
                placeholder="Start typing a name or email..."
                className="pl-8 bg-[#050003] border-[#2A0F3D] text-[#39FF14] font-mono placeholder:text-[#4B6B52] focus:border-[#39FF14] rounded-none"
                data-testid="lookup-search-input"
                autoComplete="off"
              />

              {/* Phase 5.9.23 — Typeahead dropdown */}
              {typeaheadOpen && (typeaheadResults.length > 0 || typeaheadLoading) && (
                <div
                  className="absolute z-30 left-0 right-0 top-full mt-1 bg-[#050003] border border-[#00F0FF]/60 rounded-none shadow-2xl max-h-80 overflow-y-auto"
                  data-testid="lookup-typeahead-dropdown"
                >
                  {typeaheadLoading && (
                    <div className="px-3 py-2 text-[10px] text-[#6B8E73] font-mono uppercase">
                      Searching…
                    </div>
                  )}
                  {!typeaheadLoading && typeaheadResults.map((u, idx) => {
                    const initials = (u.name || u.email || '?')
                      .split(/\s+|@/)
                      .filter(Boolean)
                      .slice(0, 2)
                      .map((p) => p[0]?.toUpperCase() || '')
                      .join('') || '?';
                    const active = idx === typeaheadActiveIdx;
                    return (
                      <button
                        key={u.id}
                        type="button"
                        onClick={() => pickTypeaheadResult(u)}
                        onMouseEnter={() => setTypeaheadActiveIdx(idx)}
                        className={`w-full text-left px-3 py-2 flex items-center gap-3 border-b border-[#2A0F3D]/40 last:border-b-0 transition-colors ${
                          active ? 'bg-[#00F0FF]/15' : 'hover:bg-[#2A0F3D]/30'
                        }`}
                        data-testid={`lookup-typeahead-row-${u.id}`}
                      >
                        <div className="w-8 h-8 shrink-0 flex items-center justify-center font-mono text-xs font-bold border border-[#39FF14] text-[#39FF14]">
                          {initials}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-baseline gap-2">
                            <span className="text-[#39FF14] font-mono text-xs truncate">{u.name || '—'}</span>
                            {u.region && <span className="text-[#6B8E73] font-mono text-[9px] uppercase">{u.region}</span>}
                            {!u.active && <span className="text-[#FF3B30] font-mono text-[9px] uppercase">INACTIVE</span>}
                          </div>
                          <div className="text-[#A3C7A8]/70 font-mono text-[10px] truncate">{u.email}</div>
                        </div>
                        <span className="font-mono text-[9px] uppercase shrink-0 tracking-wider text-[#00F0FF]">
                          {u.role}
                        </span>
                      </button>
                    );
                  })}
                  {!typeaheadLoading && typeaheadResults.length > 0 && (
                    <div className="px-3 py-1.5 text-[9px] text-[#6B8E73] font-mono uppercase border-t border-[#2A0F3D]">
                      ↑↓ navigate · enter select · esc close
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {searchedUser && searchedBalance && (
            <div className="space-y-4" data-testid="lookup-result">
              <div className="bg-[#050003] border border-[#39FF14] p-4">
                <p className="text-[#39FF14] font-mono text-sm font-bold">
                  <Link
                    to={`/dispatch/admin/users?openUser=${searchedUser.id}&from=points-admin`}
                    className="hover:underline"
                    data-testid="points-admin-customer-link"
                  >
                    {searchedUser.email}
                  </Link>
                  {' '}<span className="text-[#A3C7A8] text-xs">({searchedUser.role})</span>
                </p>
                <p className="text-[#FF9F0A] font-mono text-2xl mt-1" data-testid="searched-balance">
                  {searchedBalance.balance.toLocaleString()} PTS
                </p>
                {Object.keys(searchedBalance.by_region || {}).length > 0 && (
                  <p className="text-[#4B6B52] font-mono text-xs mt-1">
                    // by region: {Object.entries(searchedBalance.by_region).map(([r, b]) => `${r}=${b}`).join('  ')}
                  </p>
                )}
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div>
                  <Label className="text-[#A3C7A8] font-mono text-xs uppercase mb-1 block">PTS (positive)</Label>
                  <Input
                    type="number"
                    min={0}
                    value={adjustDelta}
                    onChange={(e) => setAdjustDelta(Math.max(0, Number(e.target.value || 0)))}
                    className="bg-[#050003] border-[#2A0F3D] text-[#39FF14] font-mono focus:border-[#39FF14] rounded-none"
                    data-testid="adjust-delta-input"
                  />
                </div>
                <div>
                  <Label className="text-[#A3C7A8] font-mono text-xs uppercase mb-1 block">REGION</Label>
                  <select
                    value={adjustRegion}
                    onChange={(e) => setAdjustRegion(e.target.value)}
                    className="bg-[#050003] border border-[#2A0F3D] text-[#39FF14] font-mono focus:border-[#39FF14] rounded-none w-full h-10 px-3"
                    data-testid="adjust-region-select"
                  >
                    {regions.map((r) => (
                      <option key={r.code} value={r.code}>{r.code}</option>
                    ))}
                  </select>
                </div>
                <div className="sm:col-span-1">
                  <Label className="text-[#A3C7A8] font-mono text-xs uppercase mb-1 block">REASON</Label>
                  <Input
                    value={adjustReason}
                    onChange={(e) => setAdjustReason(e.target.value)}
                    placeholder="e.g. Apology for late delivery #ABC123"
                    className="bg-[#050003] border-[#2A0F3D] text-[#39FF14] font-mono placeholder:text-[#4B6B52] focus:border-[#39FF14] rounded-none"
                    data-testid="adjust-reason-input"
                  />
                </div>
              </div>

              <div className="flex gap-2 justify-end">
                <Button
                  onClick={() => adjust('-')}
                  disabled={adjusting || !adjustDelta || !adjustReason.trim()}
                  variant="outline"
                  className="border-[#FF3B30] text-[#FF3B30] hover:bg-[rgba(255,59,48,0.08)] rounded-none font-mono"
                  data-testid="deduct-btn"
                >
                  <Minus className="w-4 h-4 mr-2" />
                  DEDUCT
                </Button>
                <Button
                  onClick={() => adjust('+')}
                  disabled={adjusting || !adjustDelta || !adjustReason.trim()}
                  className="bg-[#39FF14] text-[#050003] font-bold hover:bg-[#4AF626] rounded-none"
                  data-testid="award-btn"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  AWARD
                </Button>
              </div>

              <div className="mt-4">
                <p className="text-[#A3C7A8] font-mono text-xs uppercase mb-2 flex items-center gap-2">
                  <History className="w-3 h-3" /> RECENT HISTORY
                </p>
                <div className="bg-[#050003] border border-[#2A0F3D] max-h-72 overflow-auto" data-testid="history-list">
                  {(searchedBalance.history || []).length === 0 ? (
                    <p className="text-[#4B6B52] font-mono text-xs p-3">// no entries</p>
                  ) : (
                    (searchedBalance.history || []).map((h) => (
                      <div key={h.id} className="border-b border-[#2A0F3D] last:border-0 px-3 py-2 flex items-center justify-between gap-2">
                        <div className="min-w-0 flex-1">
                          <p className="text-[#39FF14] font-mono text-xs truncate">{h.reason}</p>
                          <p className="text-[#4B6B52] font-mono text-[10px]">
                            {new Date(h.created_at).toLocaleString()} • {h.source} • {h.actor}
                          </p>
                        </div>
                        <span
                          className={`font-mono text-sm font-bold ${
                            h.delta >= 0 ? 'text-[#39FF14]' : 'text-[#FF3B30]'
                          }`}
                        >
                          {h.delta >= 0 ? '+' : ''}{h.delta}
                        </span>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default PointsAdminPage;
